import { Modal } from '../core';
import { Button } from '../core/Button';
import { useState } from 'react';
import { useCloseMonthAdmin } from '../../graphql/hooks/useAdminDashboard';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: {
    name: string;
    userId: string;
    year: number;
    month: string;
    monthToClose: number;
    jobTitle: string;
    status: string;
    internalNumber: string | number;
    entryDate: string;
    monthHours: number;
    fixedDeduction: number;
    nightWork: number;
    emergencyService: number;
    expenses: number;
    sundayWork: number;
    timeBalance: number | string;
    closingComment?: string;
  };
}

export function PayrollConfirmModal({
  isOpen,
  onClose,
  data
}: Props) {
  const [overtimeToPay, setOvertimeToPay] = useState<number | ''>('');
  const [adjustOvertime, setAdjustOvertime] = useState<number | ''>('');
  const [comment, setComment] = useState('');
  const { mutate: closeMonth } = useCloseMonthAdmin();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='xl'
      title="Monat für Lohnbüro speichern"
    >
      <div className="space-y-6">

        {/* Top Info Grid */}
        <div className="grid grid-cols-2 gap-x-12 gap-y-6 text-[14px]">

          <div>
            <p className="text-[#6b6b6b]">Name</p>
            <p className="text-[#1c1c1c] py-1">{data.name}</p>
          </div>

          <div>
            <p className="text-[#6b6b6b]">Monat</p>
            <p className="text-[#1c1c1c] py-1">{data.month}</p>
          </div>

          <div>
            <p className="text-[#6b6b6b]">Beruf</p>
            <p className="text-[#1c1c1c] py-1">{data.jobTitle}</p>
          </div>

          <div>
            <p className="text-[#6b6b6b]">Status</p>
            <p className="text-[#1c1c1c] py-1">{data.status}</p>
          </div>

          <div>
            <p className="text-[#6b6b6b]">Person-Nr.</p>
            <p className="text-[#1c1c1c] py-1">{data.internalNumber}</p>
          </div>

          <div>
            <p className="text-[#6b6b6b]">Eintritt</p>
            <p className="text-[#1c1c1c] py-1">
              {data.entryDate
                ? new Date(data.entryDate).toLocaleDateString("de-DE")
                : "-"}
            </p>
          </div>
        </div>

        <hr className="border-[#e9e9e9]" />

        {/* Values Grid */}
        <div className="grid grid-cols-3 gap-x-12 gap-y-6 text-[14px]">

          <div>
            <p className="text-[#6b6b6b]">Soll-Stunden</p>
            <p className="text-[#1c1c1c] py-1">{data.monthHours}</p>
          </div>

          <div>
            <p className="text-[#6b6b6b]">Fester Std-Abzug</p>
            <p className="text-[#1c1c1c] py-1">{data.fixedDeduction} Std</p>
          </div>

          <div>
            <p className="text-[#6b6b6b]">Nachtarbeit</p>
            <p className="text-[#1c1c1c] py-1">{data.nightWork}</p>
          </div>

          <div>
            <p className="text-[#6b6b6b]">Notdienst</p>
            <p className="text-[#1c1c1c] py-1">{data.emergencyService}</p>
          </div>

          <div>
            <p className="text-[#6b6b6b]">Spesen</p>
            <p className="text-[#1c1c1c] py-1">{data.expenses}</p>
          </div>

          <div>
            <p className="text-[#6b6b6b]">Sonntagsarbeit</p>
            <p className="text-[#1c1c1c] py-1">{data.sundayWork}</p>
          </div>

          <div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[14px] font-medium bg-green-50 text-green-600 border border-green-200 mb-1">
              Überstunden zur Auszahlung
            </span>
            <input
              type="number"
              value={overtimeToPay}
              placeholder="0"
              onChange={(e) =>
                setOvertimeToPay(e.target.value === '' ? '' : Number(e.target.value))
              }
              className="w-full h-[38px] px-3 border border-green-600 rounded-[6px] mt-1"
            />
          </div>

          <div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[14px] font-medium bg-red-50 text-red-600 border border-red-200 mb-1">
              Überstundenabzug
            </span>
            <input
              type="number"
              value={adjustOvertime}
              placeholder="0"
              onChange={(e) =>
                setAdjustOvertime(e.target.value === '' ? '' : Number(e.target.value))
              }
              className="w-full h-[38px] px-3 border border-red-600 rounded-[6px] mt-1"
            />
          </div>

          <div>
            <p className="text-[#6b6b6b]">Aktuelles Zeitguthaben</p>
            <p className="text-[#1c1c1c] py-1">
              {data.timeBalance} Std
            </p>
          </div>
        </div>
        {data.closingComment && (
          <div>
            <p className="text-[#6b6b6b] text-[14px] mb-2">Anmerkung Mitarbeiter</p>
            <div className="px-4 py-3 bg-[#f5f5f5] rounded-[8px] text-[14px] text-[#1c1c1c]">
              {data.closingComment}
            </div>
          </div>
        )}
        {/* Comment */}
        <div>
          <p className="text-[#6b6b6b] text-[14px] mb-2">Bemerkung</p>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            className="w-full box-border px-4 py-2 border border-[#e0e0e0] rounded-[8px]"
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="secondary" onClick={onClose}>
            Schließen
          </Button>

          <Button
            variant="primary"
            onClick={() => {
              closeMonth(
                {
                  userId: data.userId,
                  month: data.monthToClose,
                  year: data.year,
                  comment,
                  payed_overtime_hours: overtimeToPay || 0,
                  adjust_overtime: adjustOvertime || 0,
                },
                {
                  onSuccess: () => {
                    onClose();
                  },
                }
              );
            }}
          >
            Speichern
          </Button>
        </div>
      </div>
    </Modal>
  );
}